import { useMemo } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts'
import type { CSSProperties } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useAdminAllSubmissions } from '../hooks/useAdminAllSubmissions'
import { useAdminApplications } from '../hooks/useAdminApplications'
import type { ApplicationSubmissionDocument } from '@/types'

const tooltipContentStyle: CSSProperties = {
  background: 'var(--popover)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  color: 'var(--popover-foreground)',
}
const tooltipLabelStyle: CSSProperties = { color: 'var(--popover-foreground)' }
const tooltipItemStyle: CSSProperties = { color: 'var(--popover-foreground)' }

type SubmissionWithId = ApplicationSubmissionDocument & { id: string }

function getMonthKey(val: unknown): string | null {
  try {
    let date: Date | null = null
    if (val instanceof Date) date = val
    else if (typeof val === 'object' && val !== null && 'toDate' in val) {
      date = (val as { toDate: () => Date }).toDate()
    }
    if (!date) return null
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
  } catch {
    return null
  }
}

function buildMonthlyData(submissions: SubmissionWithId[]) {
  const now = new Date()
  const months: { key: string; label: string }[] = []
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    const label = d.toLocaleString('default', { month: 'short', year: '2-digit' })
    months.push({ key, label })
  }
  const counts: Record<string, number> = {}
  for (const { key } of months) counts[key] = 0
  for (const s of submissions) {
    const k = getMonthKey(s.submittedAt)
    if (k && k in counts) counts[k] = (counts[k] ?? 0) + 1
  }
  return months.map(({ key, label }) => ({ month: label, count: counts[key] }))
}

function buildStatusData(submissions: SubmissionWithId[]) {
  const counts = {
    pending_payment: 0,
    pending: 0,
    reviewing: 0,
    accepted: 0,
    waitlisted: 0,
    rejected: 0,
  }
  for (const s of submissions) counts[s.status]++
  return [
    { name: 'Pending Payment', value: counts.pending_payment, fill: 'var(--chart-5)' },
    { name: 'Pending',          value: counts.pending,         fill: 'var(--chart-2)' },
    { name: 'Reviewing',        value: counts.reviewing,       fill: 'var(--chart-1)' },
    { name: 'Accepted',         value: counts.accepted,        fill: 'var(--chart-3)' },
    { name: 'Waitlisted',       value: counts.waitlisted,      fill: 'var(--chart-2)' },
    { name: 'Rejected',         value: counts.rejected,        fill: 'var(--chart-4)' },
  ].filter((d) => d.value > 0)
}

function buildByApplicationData(
  submissions: SubmissionWithId[],
  appTitleById: Record<string, string>,
) {
  const counts: Record<string, number> = {}
  for (const s of submissions) {
    counts[s.applicationId] = (counts[s.applicationId] ?? 0) + 1
  }
  return Object.entries(counts)
    .map(([id, count]) => ({ application: appTitleById[id] ?? id, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8)
}

function thisMonthCount(submissions: SubmissionWithId[]): number {
  const now = new Date()
  const key = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  return submissions.filter((s) => getMonthKey(s.submittedAt) === key).length
}

interface StatCardProps {
  title: string
  value: string | number
  loading: boolean
}

function StatCard({ title, value, loading }: StatCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-9 w-24" />
        ) : (
          <p className="text-4xl font-bold">{value}</p>
        )}
      </CardContent>
    </Card>
  )
}

export function ApplicationsDashboardPage() {
  const { submissions, loading: loadingSubs, error: errorSubs } = useAdminAllSubmissions()
  const { applications, loading: loadingApps, error: errorApps } = useAdminApplications()

  const loading = loadingSubs || loadingApps
  const error = errorSubs ?? errorApps

  const appTitleById = useMemo(() => {
    const map: Record<string, string> = {}
    for (const a of applications) map[a.id] = a.title
    return map
  }, [applications])

  const openCount = useMemo(
    () => applications.filter((a) => a.status === 'open').length,
    [applications],
  )

  const reviewQueue = useMemo(
    () => submissions.filter((s) => s.status === 'pending' || s.status === 'reviewing').length,
    [submissions],
  )

  const submittedThisMonth = useMemo(() => thisMonthCount(submissions), [submissions])

  const monthlyData = useMemo(() => buildMonthlyData(submissions), [submissions])
  const statusData  = useMemo(() => buildStatusData(submissions),  [submissions])
  const byAppData   = useMemo(
    () => buildByApplicationData(submissions, appTitleById),
    [submissions, appTitleById],
  )

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold">Applications Dashboard</h1>
        <p className="text-muted-foreground mt-1">Submission trends and review queue overview.</p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Total Submissions"   value={submissions.length} loading={loading} />
        <StatCard title="Open Applications"   value={openCount}          loading={loading} />
        <StatCard title="In Review Queue"     value={reviewQueue}        loading={loading} />
        <StatCard title="Submitted This Month" value={submittedThisMonth} loading={loading} />
      </div>

      {/* Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Submissions over time */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Submissions Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-48 w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={monthlyData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} className="fill-muted-foreground" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} className="fill-muted-foreground" />
                  <Tooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} cursor={{ fill: 'var(--muted)', opacity: 0.3 }} />
                  <Bar dataKey="count" name="Submissions" fill="var(--chart-1)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Submissions by status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Submissions by Status</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-center">
            {loading ? (
              <Skeleton className="h-48 w-48 rounded-full" />
            ) : statusData.length === 0 ? (
              <p className="text-sm text-muted-foreground py-16">No data yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                    labelLine={false}
                  >
                    {statusData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} cursor={{ fill: 'var(--muted)', opacity: 0.3 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top applications by submissions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Top Applications by Submissions</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-48 w-full" />
          ) : byAppData.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8">No data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(160, byAppData.length * 36)}>
              <BarChart
                data={byAppData}
                layout="vertical"
                margin={{ top: 4, right: 24, left: 8, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="application" width={140} tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} cursor={{ fill: 'var(--muted)', opacity: 0.3 }} />
                <Bar dataKey="count" name="Submissions" fill="var(--chart-3)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
